import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import axios from 'axios';
import './App.css';

function Stores() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [stores, setStores] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('available'); // 'available' or 'checked'
  const [checkedStores, setCheckedStores] = useState([]);
  const [showCheckedModal, setShowCheckedModal] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState(''); // 'success' or 'error'

  // Column filter states
  const [selectedGroupStatusFilter, setSelectedGroupStatusFilter] = useState('');
  const [showGroupStatusDropdown, setShowGroupStatusDropdown] = useState(false);
  const [selectedGroupNameFilter, setSelectedGroupNameFilter] = useState('');
  const [showGroupNameDropdown, setShowGroupNameDropdown] = useState(false);

  // Get current user from localStorage
  const getCurrentUser = () => {
    try {
      const authData = localStorage.getItem('auth');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.user;
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }
    return null;
  };

  const handleLogout = () => {
    // Clear authentication data from localStorage
    localStorage.removeItem('auth');
    // Redirect to login (AuthWrapper will handle showing login page)
    window.location.reload();
  };

  const fetchStores = async () => {
    setLoading(true);
    setError('');

    try {
      // Fetch stores data directly from Impact API via backend
      const storeResponse = await axios.post('/api/stores/fetch/');
      // Also fetch checked stores count to update the button counter
      const checkedResponse = await axios.get('/api/stores/checked/');

      if (storeResponse.data.success) {
        setStores(storeResponse.data.data);
        setFilteredStores(storeResponse.data.data);
        setTotalPages(Math.ceil(storeResponse.data.data.length / pageSize));
        setCurrentPage(1);

        // Update checked stores counter
        if (checkedResponse.data.success) {
          setCheckedStores(checkedResponse.data.data);
        }
      } else {
        setError(storeResponse.data.error);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch stores data');
    }

    setLoading(false);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    setTotalPages(Math.ceil(filteredStores.length / newSize));
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);

    if (searchValue.trim() === '') {
      setFilteredStores(stores);
      setTotalPages(Math.ceil(stores.length / pageSize));
    } else {
      const filtered = stores.filter(store =>
        (store.Id && store.Id.toString().toLowerCase().includes(searchValue.toLowerCase())) ||
        (store.Name && store.Name.toLowerCase().includes(searchValue.toLowerCase()))
      );
      setFilteredStores(filtered);
      setTotalPages(Math.ceil(filtered.length / pageSize));
    }
  };

  const handleStoreProducts = () => {
    if (selectedRow !== null) {
      const globalIndex = selectedRow;
      const pageIndex = globalIndex % pageSize;
      const selectedStore = getCurrentPageData()[pageIndex];

      if (selectedStore && selectedStore.Group_CatalogId) {
        // Store the selected store in sessionStorage and navigate to store products page
        sessionStorage.setItem('selectedStore', JSON.stringify(selectedStore));
        navigate('/store-products');
      } else {
        alert('Selected store does not have a valid Catalog ID');
      }
    }
  };

  const downloadExcel = async () => {
    try {
      const response = await axios.post('/api/stores/download/', { stores: filteredStores }, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'impact_stores.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download Excel');
    }
  };

  const checkStore = async () => {
    if (selectedRow === null) return;

    const globalIndex = selectedRow;
    const pageIndex = globalIndex % pageSize;
    const selectedStore = getCurrentPageData()[pageIndex];

    if (!selectedStore) return;

    try {
      setLoading(true);
      const response = await axios.post('/api/stores/check/', {
        store_data: selectedStore
      });

      if (response.data.success) {
        // Remove from current view
        const newFilteredStores = filteredStores.filter((_, index) => index !== selectedRow);
        setFilteredStores(newFilteredStores);
        setSelectedRow(null);
        setTotalPages(Math.ceil(newFilteredStores.length / pageSize));

        if (currentPage > Math.ceil(newFilteredStores.length / pageSize) && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }

        showFeedback(`Store "${selectedStore.Name}" checked successfully.`, 'success');
      } else {
        showFeedback(`Failed to check store: ${response.data.error}`, 'error');
      }
    } catch (err) {
      showFeedback(`Error checking store: ${err.response?.data?.error || err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCheckedStores = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/stores/checked/');

      if (response.data.success) {
        setCheckedStores(response.data.data);
        setViewMode('checked');
      } else {
        setError(response.data.error);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch checked stores');
    } finally {
      setLoading(false);
    }
  };

  const uncheckStore = async (storeId) => {
    try {
      setLoading(true);
      const response = await axios.post('/api/stores/uncheck/', {
        store_id: storeId
      });

      if (response.data.success) {
        // Refresh the checked stores list
        setCheckedStores(checkedStores.filter(store => store.Id !== storeId));
        showFeedback('Store unchecked and moved back to available stores.', 'success');
      } else {
        showFeedback(`Failed to uncheck store: ${response.data.error}`, 'error');
      }
    } catch (err) {
      showFeedback(`Error unchecking store: ${err.response?.data?.error || err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const switchToAvailableView = () => {
    setViewMode('available');
    setFilteredStores(stores);
    setTotalPages(Math.ceil(stores.length / pageSize));
    setCurrentPage(1);
    setSelectedRow(null);
  };

  const handleMarkAsChecked = async (store) => {
    try {
      setLoading(true);
      const response = await axios.post('/api/stores/check/', {
        store_data: store
      });

      if (response.data.success) {
        // Remove from current view
        const newFilteredStores = filteredStores.filter(s => s.Id !== store.Id);
        setFilteredStores(newFilteredStores);
        setTotalPages(Math.ceil(newFilteredStores.length / pageSize));

        // Update checked stores list by fetching the current checked stores
        try {
          const checkedResponse = await axios.get('/api/stores/checked/');
          if (checkedResponse.data.success) {
            setCheckedStores(checkedResponse.data.data);
          }
        } catch (checkedErr) {
          console.error('Failed to fetch updated checked stores count:', checkedErr);
        }

        // If we're on a page that no longer exists, go to the last valid page
        if (currentPage > Math.ceil(newFilteredStores.length / pageSize) && currentPage > 1) {
          setCurrentPage(Math.ceil(newFilteredStores.length / pageSize));
        }

        showFeedback(`Store "${store.Name}" checked successfully.`, 'success');
      } else {
        showFeedback(`Failed to check store: ${response.data.error}`, 'error');
      }
    } catch (err) {
      showFeedback(`Error checking store: ${err.response?.data?.error || err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showFeedback = (message, type) => {
    setFeedbackMessage(message);
    setFeedbackType(type);
    // Auto-hide after 3 seconds
    setTimeout(() => {
      setFeedbackMessage('');
      setFeedbackType('');
    }, 3000);
  };

  const openCheckedModal = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/stores/checked/');

      if (response.data.success) {
        setCheckedStores(response.data.data);
        setShowCheckedModal(true);
      } else {
        showFeedback(`Failed to load checked stores: ${response.data.error}`, 'error');
      }
    } catch (err) {
      showFeedback(`Error loading checked stores: ${err.response?.data?.error || err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Column filter handlers - Group Status
  const getUniqueGroupStatuses = () => {
    const statuses = new Set();
    filteredStores.forEach(store => {
      if (store.Group_Status) {
        statuses.add(store.Group_Status);
      }
    });
    return Array.from(statuses).sort();
  };

  const handleGroupStatusFilter = (statusValue) => {
    setSelectedGroupStatusFilter(statusValue);
    setCurrentPage(1);
    setTimeout(() => {
      applyColumnFilters();
      setSelectedRow(null);
    }, 10);
  };

  // Column filter handlers - Group Name
  const getUniqueGroupNames = () => {
    const names = new Set();
    filteredStores.forEach(store => {
      if (store.Group_Name) {
        names.add(store.Group_Name);
      }
    });
    return Array.from(names).sort();
  };

  const handleGroupNameFilter = (nameValue) => {
    setSelectedGroupNameFilter(nameValue);
    setCurrentPage(1);
    setTimeout(() => {
      applyColumnFilters();
      setSelectedRow(null);
    }, 10);
  };

  // Apply all column filters (AND logic with search)
  const applyColumnFilters = () => {
    let filtered = stores;

    // First apply search
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(store =>
        (store.Id && store.Id.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
        (store.Name && store.Name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply Group Status filter
    if (selectedGroupStatusFilter) {
      filtered = filtered.filter(store => store.Group_Status === selectedGroupStatusFilter);
    }

    // Apply Group Name filter
    if (selectedGroupNameFilter) {
      filtered = filtered.filter(store => store.Group_Name === selectedGroupNameFilter);
    }

    setFilteredStores(filtered);
    setTotalPages(Math.ceil(filtered.length / pageSize));
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedGroupStatusFilter('');
    setSelectedGroupNameFilter('');
    setShowGroupStatusDropdown(false);
    setShowGroupNameDropdown(false);
    setCurrentPage(1);
    setSelectedRow(null);
    setFilteredStores(stores);
    setTotalPages(Math.ceil(stores.length / pageSize));
  };

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredStores.slice(startIndex, endIndex);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    let items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <button
          key={number}
          className={`mx-1 px-3 py-1 rounded ${
            number === currentPage
              ? 'bg-perzsi-yellow text-perzsi-gray font-semibold'
              : 'bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50'
          }`}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </button>
      );
    }

    return (
      <div className="flex justify-center items-center mt-6">
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        >
          First
        </button>
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {items}
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          Last
        </button>
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
      {/* Header with Perzsi Branding */}
      <header className="perzsi-gradient shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div
                className="cursor-pointer"
                onClick={() => navigate('/')}
              >
                <img
                  src="/perzsi-icon-black.png"
                  alt="Perzsi.com"
                  className="h-12 w-auto"
                />
                <p className="text-yellow-100">Impact Data Management - Stores</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-white text-right">
                <p className="text-sm opacity-90">Comprehensive Store Data Management</p>
                {getCurrentUser() && (
                  <p className="text-xs opacity-75">
                    Logged in as: {getCurrentUser().email}
                  </p>
                )}
              </div>
              <button
                onClick={toggleTheme}
                className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg transition-colors duration-200"
                title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
              >
                {isDark ? '☀️' : '🌙'}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="w-full px-4 py-8">
        {/* Feedback Message */}
        {feedbackMessage && (
          <div className={`mb-6 px-4 py-3 rounded-lg ${
            feedbackType === 'success'
              ? 'bg-green-100 border border-green-400 text-green-700'
              : 'bg-red-100 border border-red-400 text-red-700'
          }`}>
            <div className="flex items-center">
              <span className="font-medium">
                {feedbackType === 'success' ? '✓' : '✗'} {feedbackMessage}
              </span>
            </div>
          </div>
        )}

        {/* Control Panel */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Fetch Store Data</h3>
            <p className={`mb-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Retrieve fresh store and group data directly from the Impact API.
            </p>
            <button
              className="perzsi-button-primary w-full"
              onClick={fetchStores}
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-perzsi-gray" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Fetching Stores...
                </span>
              ) : (
                'Fetch Store Data'
              )}
            </button>
          </div>

          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Search & Settings</h3>
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Search by Id or Name:
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search stores..."
                className="perzsi-input"
              />
            </div>
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Rows per page:
              </label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                className="perzsi-input"
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={300}>300</option>
              </select>
            </div>
            {filteredStores.length > 0 && (
              <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <p><strong>Total Store-Groups: {filteredStores.length}</strong></p>
                <p><strong>Showing: {getCurrentPageData().length} of {filteredStores.length}</strong></p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        {filteredStores.length > 0 && (
          <div className="flex flex-wrap gap-4 mb-6 justify-between">
            <div className="flex flex-wrap gap-4">
              <button
                className="bg-perzsi-yellow hover:bg-yellow-600 text-perzsi-gray font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
                onClick={downloadExcel}
              >
                Download Stores as Excel
              </button>

              {selectedRow !== null && (
                <button
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
                  onClick={handleStoreProducts}
                >
                  Store Products
                </button>
              )}

              <button
                className="font-semibold py-2 px-4 rounded-lg transition-colors duration-200 bg-blue-600 hover:bg-blue-700 text-white"
                onClick={openCheckedModal}
              >
                View Checked Stores ({checkedStores.length})
              </button>
            </div>
            <div>
              <button
                className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
                onClick={resetFilters}
              >
                Reset Filters
              </button>
            </div>
          </div>
        )}

        {/* Top Pagination */}
        {filteredStores.length > 0 && totalPages > 1 && (
          <div className="flex justify-center items-center mb-4">
            <button
              className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              First
            </button>
            <button
              className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {(() => {
              let items = [];
              const maxVisiblePages = 5;
              let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
              let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

              if (endPage - startPage + 1 < maxVisiblePages) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
              }

              for (let number = startPage; number <= endPage; number++) {
                items.push(
                  <button
                    key={number}
                    className={`mx-1 px-3 py-1 rounded ${
                      number === currentPage
                        ? 'bg-perzsi-yellow text-perzsi-gray font-semibold'
                        : 'bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => handlePageChange(number)}
                  >
                    {number}
                  </button>
                );
              }
              return items;
            })()}
            <button
              className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
            <button
              className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </button>
          </div>
        )}

        {/* Stores Table */}
        {filteredStores.length > 0 && (
          <div className="perzsi-table-container">
            <div className="overflow-x-auto">
              <table className="perzsi-table">
                <thead>
                  <tr>
                    <th className="w-16">
                      <input
                        type="radio"
                        name="stores-select-all"
                        checked={false}
                        readOnly
                        className="w-5 h-5 rounded border-2 border-gray-300"
                      />
                    </th>
                    <th>Id</th>
                    <th>Name</th>
                    <th>DateLastUpdated</th>
                    <th>StoreUri</th>
                    <th>Group_Id</th>
                    <th className="relative">
                      <div className="flex items-center justify-between">
                        <span>Group_Name</span>
                        <div className="relative">
                          <button
                            onClick={() => setShowGroupNameDropdown(!showGroupNameDropdown)}
                            className="ml-2 px-1 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded"
                          >
                            ▼
                          </button>
                          {showGroupNameDropdown && (
                            <div className={`absolute top-full left-0 z-10 w-48 ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} rounded shadow-lg`}>
                              <div className="py-1">
                                <button
                                  onClick={() => {
                                    setSelectedGroupNameFilter('');
                                    setShowGroupNameDropdown(false);
                                    setCurrentPage(1);
                                    applyColumnFilters();
                                  }}
                                  className={`w-full text-left px-4 py-2 text-sm ${isDark ? 'hover:bg-gray-600 text-white' : 'hover:bg-gray-100 text-gray-900'}`}
                                >
                                  All Groups
                                </button>
                                {getUniqueGroupNames().map(name => (
                                  <button
                                    key={name}
                                    onClick={() => {
                                      handleGroupNameFilter(name);
                                      setShowGroupNameDropdown(false);
                                    }}
                                    className={`w-full text-left px-4 py-2 text-sm ${isDark ? 'hover:bg-gray-600 text-white' : 'hover:bg-gray-100 text-gray-900'} ${
                                      selectedGroupNameFilter === name ? (isDark ? 'bg-blue-900/50 font-medium text-blue-200' : 'bg-blue-50 font-medium text-blue-900') : ''
                                    }`}
                                  >
                                    {name || 'N/A'}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </th>
                    <th>Group_NumberOfProducts</th>
                    <th className="relative">
                      <div className="flex items-center justify-between">
                        <span>Group_Status</span>
                        <div className="relative">
                          <button
                            onClick={() => setShowGroupStatusDropdown(!showGroupStatusDropdown)}
                            className="ml-2 px-1 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded"
                          >
                            ▼
                          </button>
                          {showGroupStatusDropdown && (
                            <div className={`absolute top-full left-0 z-10 w-48 ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} rounded shadow-lg`}>
                              <div className="py-1">
                                <button
                                  onClick={() => {
                                    setSelectedGroupStatusFilter('');
                                    setShowGroupStatusDropdown(false);
                                    setCurrentPage(1);
                                    applyColumnFilters();
                                  }}
                                  className={`w-full text-left px-4 py-2 text-sm ${isDark ? 'hover:bg-gray-600 text-white' : 'hover:bg-gray-100 text-gray-900'}`}
                                >
                                  All Statuses
                                </button>
                                {getUniqueGroupStatuses().map(status => (
                                  <button
                                    key={status}
                                    onClick={() => {
                                      handleGroupStatusFilter(status);
                                      setShowGroupStatusDropdown(false);
                                    }}
                                    className={`w-full text-left px-4 py-2 text-sm ${isDark ? 'hover:bg-gray-600 text-white' : 'hover:bg-gray-100 text-gray-900'} ${
                                      selectedGroupStatusFilter === status ? (isDark ? 'bg-blue-900/50 font-medium text-blue-200' : 'bg-blue-50 font-medium text-blue-900') : ''
                                    }`}
                                  >
                                    {status || 'N/A'}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </th>
                    <th>Group_CatalogId</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getCurrentPageData().map((store, index) => {
                    const globalIndex = (currentPage - 1) * pageSize + index;
                    const isSelected = selectedRow === globalIndex;

                    return (
                      <tr key={index} className={isSelected ? (isDark ? 'bg-blue-900/30' : 'bg-yellow-50') : (isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50')}>
                        <td>
                          <input
                            type="radio"
                            name="stores-select"
                            checked={isSelected}
                            onChange={() => setSelectedRow(globalIndex)}
                            className="w-5 h-5 rounded border-2 border-gray-300 text-perzsi-yellow focus:ring-2 focus:ring-perzsi-yellow focus:border-perzsi-yellow cursor-pointer"
                          />
                        </td>
                        <td>{store.Id}</td>
                        <td className="font-medium">{store.Name}</td>
                        <td>{store.DateLastUpdated}</td>
                        <td className="max-w-xs">
                          <a
                            href={store.StoreUri}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline font-medium"
                          >
                            Link
                          </a>
                        </td>
                        <td>{store.Group_Id || 'N/A'}</td>
                        <td>{store.Group_Name || 'N/A'}</td>
                        <td>{store.Group_NumberOfProducts || 'N/A'}</td>
                        <td>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            store.Group_Status?.toLowerCase() === 'active'
                              ? 'bg-green-100 text-green-800'
                              : store.Group_Status?.toLowerCase() === null ? 'bg-gray-100 text-gray-600' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {store.Group_Status || 'N/A'}
                          </span>
                        </td>
                        <td>{store.Group_CatalogId || 'N/A'}</td>
                        <td>
                          <div className="flex flex-wrap gap-1">
                            {viewMode === 'available' && (
                              <button
                                className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded text-xs transition-colors duration-200"
                                onClick={() => handleMarkAsChecked(store)}
                                disabled={loading}
                              >
                                Check
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {renderPagination()}

        {/* Back Button */}
        <div className="mt-6 text-center">
          <button
            className="perzsi-button-secondary"
            onClick={() => navigate('/')}
          >
            ← Back to Home
          </button>
        </div>
      </div>

      {/* Checked Stores Modal */}
      {showCheckedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`relative w-full max-w-2xl mx-4 rounded-lg shadow-xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Modal Header */}
            <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>
                  Checked Stores ({checkedStores.length})
                </h2>
                <button
                  onClick={() => setShowCheckedModal(false)}
                  className={`p-2 rounded-lg hover:bg-gray-200 ${isDark ? 'hover:bg-gray-700' : ''}`}
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="max-h-96 overflow-y-auto px-6 py-4">
              {checkedStores.length === 0 ? (
                <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  No checked stores found.
                </div>
              ) : (
                <div className="space-y-3">
                  {checkedStores.map((store, index) => (
                    <div
                      key={store.Id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${
                          isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                        } text-sm font-medium`}>
                          {index + 1}
                        </span>
                        <div>
                          <div className={`font-medium ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>
                            {store.Name}
                          </div>
                          <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            ID: {store.Id}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => uncheckStore(store.Id)}
                        disabled={loading}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors duration-200 ${
                          isDark
                            ? 'bg-gray-600 hover:bg-gray-500 text-white'
                            : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        } disabled:opacity-50`}
                      >
                        Uncheck
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className={`px-6 py-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
              <button
                onClick={() => setShowCheckedModal(false)}
                className={`w-full py-2 px-4 rounded-lg font-semibold transition-colors duration-200 ${
                  isDark
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Stores;
